import React, { useEffect, useMemo, useRef, useState } from 'react';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import ArrowRightIcon from '@mui/icons-material/ChevronRightRounded';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import Menu from '@mui/material/Menu';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';

import type { ELeadStage } from 'src/services/api';
import { LEAD_STAGES } from 'src/services/api';
import { Flex, FlexColumn } from 'src/components/flex';
import { LeadsIcon, PropertiesIcon, UsersIcon } from 'src/icons';
import { StageChip } from 'src/components/input/select-lead-stage/SelectLeadStage';
import SelectLabels from 'src/components/input/select-labels/SelectLabels';
import { useConversationsPageContext } from 'src/pages/landlord/conversations/context';
import AutocompleteMultiple from 'src/components/input/autocomplete-multiple/AutocompleteMultiple';
import { useAuthenticatedState } from 'src/authenticated-state/context';

enum FilteringBy {
    Assignee = 'Assignee',
    IngestionSource = 'Ingestion Source',
    LeadStage = 'Lead Stage',
    LeadLabel = 'Lead Label',
    LeadIsArchived = 'Lead Archived Status',
    Property = 'Property',
    PropertyLabel = 'Property Label',
}

const teamMemberFilters = [FilteringBy.Assignee];
const ingestionFilters = [FilteringBy.IngestionSource];
const leadFilters = [FilteringBy.LeadStage, FilteringBy.LeadLabel, FilteringBy.LeadIsArchived];
const propertyFilters = [FilteringBy.Property, FilteringBy.PropertyLabel];

type Props = {
    anchorEl?: HTMLElement | null;
}

const boolOrUndefinedFromString = (value: string) => {
    switch (value) {
        case 'true': return true;
        case 'false': return false;
        default: return undefined;
    }
};

const stringFromBoolOrUndefined = (value: boolean | undefined) => {
    switch (value) {
        case undefined: return 'undefined';
        case true: return 'true';
        case false: return 'false';
        default: throw new Error('Invalid boolean value');
    }
};

export default function ConversationsFilterModal({ anchorEl }: Props) {
    const { leadIngestionSources } = useAuthenticatedState();
    const {
        teamMembers,
        properties,
        isFilterOpen,
        setFilterOpen,
        filter,
        setFilter,
        resetFilter,
    } = useConversationsPageContext();

    const searchRef = useRef<HTMLInputElement>(null);

    const [filteringBy, setFilteringBy] = useState<FilteringBy>();

    const handleClose = () => {
        setFilterOpen(false);
        setFilteringBy(undefined);
    };

    const handleToggleLeadStage = (stage: ELeadStage) => {
        if (!filter.leadStages) {
            setFilter({ leadStages: [stage] });
        } else if (filter.leadStages.includes(stage)) {
            setFilter({ leadStages: filter.leadStages.filter(o => o !== stage) });
        } else {
            setFilter({ leadStages: [...filter.leadStages, stage] });
        }
    };

    // Focus on the search input when the modal is opened
    useEffect(() => {
        if (isFilterOpen) {
            setTimeout(() => searchRef.current?.focus(), 0);
        }
    }, [isFilterOpen]);

    const teamMemberOptions = useMemo(() => {
        return teamMembers.map(member => ({
            value: String(member.id),
            label: member.name,
        }));
    }, [teamMembers]);

    const ingestionSourceOptions = useMemo(() => {
        return leadIngestionSources.map(source => ({
            value: source,
            label: source,
        }));
    }, [leadIngestionSources]);

    const propertyOptions = useMemo(() => {
        const _propertyOptions = properties?.map(property => ({
            value: String(property.id),
            label: property.name,
        })) || [];

        return [
            { value: 'none', label: 'None' },
            ..._propertyOptions,
        ];
    }, [properties]);

    return (
        <Menu
            onClose={handleClose}
            open={isFilterOpen && !!anchorEl}
            anchorEl={anchorEl}
            slotProps={{
                root: {
                    sx: {
                        '@media (max-width: 900px)': {
                            p: 1, display: 'flex', justifyContent: 'center', alignItems: 'center'
                        }
                    }
                },
                paper: {
                    sx: {
                        width: 400,
                        maxWidth: '100%',
                        '@media (max-width: 900px)': {
                            position: 'static', width: 500, maxWidth: '100%'
                        }
                    }
                }
            }}
        >
            <Flex alignItems="center" p={2}>
                {!!filteringBy && (
                    <IconButton onClick={() => setFilteringBy(undefined)} edge="start">
                        <ArrowBackRounded />
                    </IconButton>
                )}

                <Typography>
                    Filter by
                </Typography>
                <Typography>
                    {filteringBy}
                </Typography>
            </Flex>

            {!filteringBy && (
                <div>
                    {teamMemberFilters.map((item) => (
                        <MenuItem key={item} onClick={() => setFilteringBy(item)}>
                            <ListItemIcon>
                                <UsersIcon />
                            </ListItemIcon>
                            <ListItemText>
                                <Typography>
                                    {item}
                                </Typography>
                            </ListItemText>
                            <ListItemIcon sx={{ justifyContent: 'flex-end' }}>
                                <ArrowRightIcon />
                            </ListItemIcon>
                        </MenuItem>
                    ))}

                    <Divider />

                    {ingestionFilters.map((item) => (
                        <MenuItem key={item} onClick={() => setFilteringBy(item)}>
                            <ListItemIcon>
                                <DownloadRoundedIcon />
                            </ListItemIcon>
                            <ListItemText>
                                <Typography>
                                    {item}
                                </Typography>
                            </ListItemText>
                            <ListItemIcon sx={{ justifyContent: 'flex-end' }}>
                                <ArrowRightIcon />
                            </ListItemIcon>
                        </MenuItem>
                    ))}

                    <Divider />

                    {leadFilters.map((item) => (
                        <MenuItem key={item} onClick={() => setFilteringBy(item)}>
                            <ListItemIcon>
                                <LeadsIcon />
                            </ListItemIcon>
                            <ListItemText>
                                <Typography>
                                    {item}
                                </Typography>
                            </ListItemText>
                            <ListItemIcon sx={{ justifyContent: 'flex-end' }}>
                                <ArrowRightIcon />
                            </ListItemIcon>
                        </MenuItem>
                    ))}

                    <Divider />

                    {propertyFilters.map((item) => (
                        <MenuItem key={item} onClick={() => setFilteringBy(item)}>
                            <ListItemIcon>
                                <PropertiesIcon />
                            </ListItemIcon>
                            <ListItemText>
                                <Typography>
                                    {item}
                                </Typography>
                            </ListItemText>
                            <ListItemIcon sx={{ justifyContent: 'flex-end' }}>
                                <ArrowRightIcon />
                            </ListItemIcon>
                        </MenuItem>
                    ))}
                </div>
            )}

            {!!filteringBy && (
                <div>
                    {filteringBy === FilteringBy.Assignee && (
                        <Flex p={1}>
                            <AutocompleteMultiple
                                placeholder="Select Assignees"
                                options={teamMemberOptions}
                                value={filter.assigneeIds}
                                onChange={(assigneeIds) => {
                                    setFilter({
                                        assigneeIds: assigneeIds || [],
                                    });
                                }}
                                size="small"
                                fullWidth
                                onKeyDown={(event) => {
                                    // Prevent keyboard navigation from moving focus away
                                    if (event.key === 'f' || event.key === 'F') {
                                        event.stopPropagation();
                                    }
                                }}
                            />
                        </Flex>
                    )}

                    {filteringBy === FilteringBy.IngestionSource && (
                        <Flex p={1}>
                            <AutocompleteMultiple
                                placeholder="Select Ingestion Sources"
                                options={ingestionSourceOptions}
                                value={filter.ingestionSources}
                                onChange={(ingestionSources) => {
                                    setFilter({
                                        ingestionSources: ingestionSources || [],
                                    });
                                }}
                                size="small"
                                fullWidth
                                data-testid="filter-by-ingestion-source"
                                renderOption={(optionProps, option) => (
                                    <li {...optionProps} key={option.value}>
                                        <span className="ellipsisText" style={{ display: 'block', width: '100%' }}>
                                            {option.label}
                                        </span>
                                    </li>
                                )}
                                onKeyDown={(event) => {
                                    // Prevent keyboard navigation from moving focus away
                                    if (event.key === 'f' || event.key === 'F') {
                                        event.stopPropagation();
                                    }
                                }}
                            />
                        </Flex>
                    )}

                    {filteringBy === FilteringBy.LeadStage && LEAD_STAGES.map(stage => {
                        return (
                            <MenuItem key={stage} onClick={() => handleToggleLeadStage(stage)}>
                                <Flex alignItems="center">
                                    <Checkbox
                                        checked={filter.leadStages && filter.leadStages.includes(stage)}
                                        sx={{ padding: 0, paddingLeft: 1 }}
                                    />
                                    <StageChip value={stage} />
                                </Flex>
                            </MenuItem>
                        );
                    })}

                    {filteringBy === FilteringBy.LeadLabel && (
                        <Flex p={1} flexDirection="column" gap={2}>
                            <SelectLabels
                                type="lead"
                                label="Lead label is"
                                placeholder="Select lead labels..."
                                value={filter.leadLabelIds}
                                onChange={(value) => {
                                    const excludeLeadLabelIds = filter.excludeLeadLabelIds ? filter.excludeLeadLabelIds.filter(id => !value?.includes(id)) : [];
                                    setFilter({
                                        leadLabelIds: value || [],
                                        excludeLeadLabelIds,
                                    });
                                }}
                                size="small"
                                fullWidth
                                onKeyDown={(event) => {
                                    // Prevent keyboard navigation from moving focus away
                                    if (event.key === 'f' || event.key === 'F') {
                                        event.stopPropagation();
                                    }
                                }}
                            />

                            <SelectLabels
                                type="lead"
                                label="Lead label is not"
                                placeholder="Select lead labels..."
                                value={filter.excludeLeadLabelIds}
                                onChange={(value) => {
                                    const leadLabelIds = filter.leadLabelIds ? filter.leadLabelIds.filter(id => !value?.includes(id)) : [];
                                    setFilter({
                                        leadLabelIds,
                                        excludeLeadLabelIds: value || [],
                                    });
                                }}
                                size="small"
                                fullWidth
                                onKeyDown={(event) => {
                                    // Prevent keyboard navigation from moving focus away
                                    if (event.key === 'f' || event.key === 'F') {
                                        event.stopPropagation();
                                    }
                                }}
                            />
                        </Flex>
                    )}

                    {filteringBy === FilteringBy.LeadIsArchived && (
                        <Flex px={2} py={1}>
                            <RadioGroup
                                value={stringFromBoolOrUndefined(filter.leadIsArchived)}
                                onChange={(_, value) => setFilter({ leadIsArchived: boolOrUndefinedFromString(value) })}
                            >
                                <FlexColumn rowGap={0}>
                                    <FormControlLabel value="undefined" label="Any" control={<Radio />} />
                                    <FormControlLabel value="true" label="Archived Only" control={<Radio />} />
                                    <FormControlLabel value="false" label="Unarchived Only" control={<Radio />} />
                                </FlexColumn>
                            </RadioGroup>
                        </Flex>
                    )}

                    {filteringBy === FilteringBy.Property && (
                        <Flex p={1}>
                            <AutocompleteMultiple
                                placeholder="Select properties"
                                options={propertyOptions}
                                value={filter.propertyIds}
                                onChange={(propertyIds) => {
                                    if (propertyIds?.includes('none')) {
                                        setFilter({
                                            propertyIds: [],
                                            hasNoProperty: true,
                                        });

                                        handleClose();
                                    } else {
                                        setFilter({
                                            propertyIds: propertyIds || [],
                                            hasNoProperty: false,
                                        });
                                    }
                                }}
                                size="small"
                                fullWidth
                                onKeyDown={(event) => {
                                    // Prevent keyboard navigation from moving focus away
                                    if (event.key === 'f' || event.key === 'F') {
                                        event.stopPropagation();
                                    }
                                }}
                            />
                        </Flex>
                    )}

                    {filteringBy === FilteringBy.PropertyLabel && (
                        <Flex p={1}>
                            <SelectLabels
                                type="property"
                                placeholder="Select property labels..."
                                value={filter.propertyLabelIds}
                                onChange={(propertyLabelIds) => setFilter({ propertyLabelIds: propertyLabelIds || [] })}
                                size="small"
                                fullWidth
                                onKeyDown={(event) => {
                                    // Prevent keyboard navigation from moving focus away
                                    if (event.key === 'f' || event.key === 'F') {
                                        event.stopPropagation();
                                    }
                                }}
                            />
                        </Flex>
                    )}
                </div>
            )}

            <Divider />

            <Flex width="100%" p={1} justifyContent="space-between">
                <Button
                    variant="outlined"
                    color="error"
                    onClick={() => {
                        resetFilter();
                        handleClose();
                    }}
                    size="small"
                    fullWidth
                >
                    Clear All
                </Button>
                <Button
                    variant="outlined"
                    color="dark"
                    onClick={handleClose}
                    size="small"
                    fullWidth
                >
                    Close
                </Button>
            </Flex>
        </Menu>
    );
}
